import { Component, OnInit } from '@angular/core';
import { AdManagementService } from 'src/app/services/ad-management/ad-management.service';
import { CachingService } from 'src/app/services/caching/caching.service';
import { AuthService, GoogleLoginProvider } from "angularx-social-login";
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { Ad } from 'src/app/models/ad';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.css'],
  providers: [AdManagementService, CachingService]
})

export class AdminComponent implements OnInit {
  private readonly allValues = "*";
  private immutableAds: Ad[];
  public filteredAds: Ad[];
  public searchText: string;
  public searchPrice: string;
  public searchEstateType: string;
  public searchTransactionType: string;
  public photoAssetsBaseUrl: string;
  public secret: string;

  public constructor(
    private adManagementService: AdManagementService,
    private cachingService: CachingService,
    private socialAuthService: AuthService,
    private toastr: ToastrService,
    private router: Router,
  ) {
    this.photoAssetsBaseUrl = environment.photoAssetsBaseUrl;
  }

  public ngOnInit(): void {
    this.searchText = "";
    this.searchPrice = "";
    this.searchEstateType = this.allValues;
    this.searchTransactionType = this.allValues;

    if (this.hasSecret()) {
      this.fetchAllAds();
    }
  }

  private handleError(operation: string, error: string) {
    const errorDetails = `error in ${operation}() raising ${error}`;
    console.error(errorDetails);
    this.toastr.error(errorDetails);
  }

  public hasSecret(): Boolean {
    const secret = this.cachingService.getSecret();
    return secret != null && secret != "";
  }

  public saveSecret(): void {
    this.cachingService.setSecret(this.secret);
  }

  public eraseSecret(): void {
    this.cachingService.setSecret("");
    this.cachingService.setGToken("");
  }

  public fetchToken(): void {
    this.socialAuthService
      .signIn(GoogleLoginProvider.PROVIDER_ID)
      .then(
        userData => {
          this.cachingService.setGToken(userData.idToken);
          this.fetchSecret();
        },
        error => this.handleError("fetchToken()", error)
      );
  }

  private fetchSecret(): void {
    this.adManagementService
      .getSecret(this.cachingService.getGoogleAuthToken())
      .subscribe(
        secret => {
          this.cachingService.setSecret(secret);
          this.fetchAllAds();
        },
        error => this.handleError("fetchSecret()", error)
      );
  }

  private fetchAllAds(): void {
    this.adManagementService
      .getAllAds(this.cachingService.getSecret())
      .subscribe(
        ads => {
          this.immutableAds = ads;
          this.filterAds();
        },
        error => this.handleError("fetchAllAds()", error)
      );
  }

  private isNumber(value: string | number): boolean {
    return !isNaN(Number(value.toString()));
  }

  private filterAds(): void {
    this.filteredAds = this.immutableAds
      .filter(
        ad =>
          this.searchText == ""
          || ad.description.toLowerCase().includes(this.searchText.toLowerCase())
          || ad.title.toLowerCase().includes(this.searchText.toLowerCase()))
      .filter(
        ad =>
          this.searchPrice == ""
          || !this.isNumber(this.searchPrice)
          || ad.price === Number(this.searchPrice))
      .filter(
        ad =>
          this.searchEstateType == this.allValues
          || ad.estatetype.toLowerCase().includes(this.searchEstateType.toLowerCase()))
      .filter(
        ad =>
          this.searchTransactionType == this.allValues
          || ad.transactiontype === this.searchTransactionType);
  }

  public createAd(): void {
    this.router.navigate(['edit']);
  }

  public switchAdVisibility(ad: Ad): void {
    ad.switchVisibility();
    this.adManagementService
      .putAd(ad, this.cachingService.getSecret())
      .subscribe(
        _ => {
          this.toastr.success('Visibilité de l annonce modifiée!');
        },
        error => {
          ad.switchVisibility();
          this.handleError("switchVisibility(ad: Ad)", error)
        }
      );
  }

  public removeAd(ad: Ad): void {
    this.adManagementService
      .deleteAd(ad, this.cachingService.getSecret())
      .subscribe(
        _ => {
          this.fetchAllAds();
          this.toastr.success('Annonce supprimée!');
        },
        error => this.handleError("removeAd(ad: Ad)", error)
      );
  }

  public doNotRemoveAd(): void {
    this.toastr.info('Annonce non supprimée');
  }
}
